import React, { useEffect, useState } from 'react';
import { Button, Form, FormGroup, FormFeedback, Input, Label, FormText, Row, Col, CustomInput } from 'reactstrap';
import { ErrorPasswordPolitics } from '../../../components/common/ErrorPasswordPolitics';
import { useSelector } from 'react-redux';

import { passwordStrengthChecker } from '../../../../core/helper';
import {
    DEFAULT_PRIVACY_POLICY_URL,
    DEFAULT_TERMS_OF_USE_URL,
    REGISTER_USER_ALREADY_EXISTS_MESSAGES,
    REGISTER_USER_INACTIVE_EXISTS_MESSAGE,
} from '../../../../core/constants';
import { ModalUserAlreadyExists } from '../ModalUserAlreadyExists';
import { ModalInactiveUserExists } from '../ModalInactiveUserExists';

export const FormRegister = ({ inputValues, handleInputChange, listSeniorities, onSubmitRegister, formErrors, formValid }) => {
    const register = useSelector(state => state.register);
    const systemCheck = useSelector(state => state?.systemCheck?.data?.data);
    const termsOfUseUrl = systemCheck?.terms_of_use_url || DEFAULT_TERMS_OF_USE_URL;
    const privacyPolicyUrl = systemCheck?.privacy_policy_url || DEFAULT_PRIVACY_POLICY_URL;
    const [disabled, setDisabled] = useState(true);
    const [showUserExistsModal, setShowUserExistsModal] = useState(false);
    const [showInactiveUserModal, setShowInactiveUserModal] = useState(false);

    const getRegisterErrorMessage = (error) => {
        const errorEmail = error?.data?.email?.length && error?.data?.email[0];
        return errorEmail || error?.description || error?.message;
    };

    const isUserAlreadyExistsError = (error) => {
        return REGISTER_USER_ALREADY_EXISTS_MESSAGES.includes(getRegisterErrorMessage(error));
    };

    const isUserInactiveExistsError = (error) => {
        return getRegisterErrorMessage(error) === REGISTER_USER_INACTIVE_EXISTS_MESSAGE;
    };

    const parseError = (error) => {
        const errorMessage = getRegisterErrorMessage(error);

        switch(errorMessage) {
            case 'The Account is full of Normal Users':
                return <span>
                    The account has reached its maximum number of normal users.
                </span>
            case 'The Account is full of Coaches':
                return <span>
                    The account has reached its maximum number of coaches.
                </span>
            default:
                return <span> {errorMessage} </span>;
        }
    }

    useEffect(() => {
        setDisabled(!formValid());
    }, [inputValues])

    useEffect(() => {
        if (register.error === 0) return;

        if (isUserAlreadyExistsError(register.error)) {
            setShowUserExistsModal(true);
        } else if (isUserInactiveExistsError(register.error)) {
            setShowInactiveUserModal(true);
        }
    }, [register.error])

    const showInlineError = register.error !== 0
        && !isUserAlreadyExistsError(register.error)
        && !isUserInactiveExistsError(register.error)
        && !(register.error.description && register.error.description.password_politics);
    
    return (
        <>
            <Form onSubmit={onSubmitRegister} className="form-login">
                <Row>
                    <Col sm={6}>
                        <FormGroup className='required mb-3'>
                            <Label for="first_name">First Name</Label>
                            <Input
                                type="text"
                                name="firstName"
                                id="first_name"
                                value={inputValues.firstName}
                                onChange={handleInputChange}
                                invalid={formErrors.firstName === '' ? null : true}
                            />
                            {formErrors.firstName && <FormFeedback>{formErrors.firstName}</FormFeedback>}
                        </FormGroup>
                    </Col>
                    <Col sm={6}>
                        <FormGroup className='required mb-3'>
                            <Label for="last_name">Last Name</Label>
                            <Input
                                type="text"
                                name="lastName"
                                id="last_name"
                                value={inputValues.lastName}
                                onChange={handleInputChange}
                                invalid={formErrors.lastName === '' ? null : true}
                            />
                            {formErrors.lastName && <FormFeedback>{formErrors.lastName}</FormFeedback>}
                        </FormGroup>
                    </Col>
                </Row>
                <FormGroup className='required mb-3'>
                    <Label for="email">Email Address</Label>
                    <Input
                        type="email"
                        name="email"
                        id="email"
                        value={inputValues.email}
                        onChange={handleInputChange}
                        invalid={formErrors.email === '' ? null : true}
                    />
                    {formErrors.email && <FormFeedback>{formErrors.email}</FormFeedback>}
                </FormGroup>
                <FormGroup className='required mb-3'>
                    <Label for="confirm-email">Confirm Email Address</Label>
                    <Input
                        type="email"
                        name="confirmEmail"
                        id="confirm-email"
                        value={inputValues.confirmEmail}
                        onChange={handleInputChange}
                        invalid={formErrors.confirmEmail === '' ? null : true}
                    />
                    {formErrors.confirmEmail && <FormFeedback>{formErrors.confirmEmail}</FormFeedback>}
                </FormGroup>
                <FormGroup className='required mb-3'>
                    <Label for="password">Password</Label>
                    <Input
                        type="password"
                        name="password"
                        id="password"
                        value={inputValues.password}
                        onChange={handleInputChange}
                        invalid={formErrors.password === '' ? null : true}
                    />
                    {formErrors.password && <FormFeedback>{formErrors.password}</FormFeedback>}
                    {inputValues.password && !formErrors.password &&
                        <FormText className='ml-3'>{passwordStrengthChecker(inputValues.password)}</FormText>
                    }
                </FormGroup>
                <FormGroup className='required mb-3'>
                    <Label for="confirm-pass">Confirm Password</Label>
                    <Input
                        type="password"
                        name="confirmPassword"
                        id="confirm-pass"
                        value={inputValues.confirmPassword}
                        onChange={handleInputChange}
                        invalid={formErrors.confirmPassword === '' ? null : true}
                    />
                    {formErrors.confirmPassword && <FormFeedback>{formErrors.confirmPassword}</FormFeedback>}
                </FormGroup>
                <FormGroup className='required mb-3'>
                    <Label for="select">Seniority</Label>
                    <select
                        className="form-control"
                        onChange={handleInputChange}
                        name="senioritySelect"
                        value={inputValues.senioritySelect}
                    >
                        <option value="">Select your option</option>
                        {
                            listSeniorities &&
                            listSeniorities.map(item =>
                                <option
                                    key={item.id}
                                    value={item.id}>
                                    {item.name}
                                </option>
                            )
                        }
                    </select>
                </FormGroup>
                <FormGroup className='required'>
                    <CustomInput
                        className="styled-checkbox mt-1"
                        type="checkbox"
                        id="termsAndPrivacy"
                        name='termsAndPrivacy'
                        checked={!!inputValues.termsAndPrivacy}
                        onChange={handleInputChange}
                        label={
                            <>I agree to True Advance's <a href={termsOfUseUrl} target='_blank' rel='noreferrer' className="text-danger font-weight-semibold">Terms of Use</a> and <a href={privacyPolicyUrl} target='_blank' rel='noreferrer' className="text-danger font-weight-semibold">Privacy Policy</a></>
                        }
                    />
                </FormGroup>

                {register.error !== 0 && register.error.description && register.error.description.password_politics && register.error.description.password_politics && <ErrorPasswordPolitics errorPasswordPolitics={register.error.description.password_politics} />}

                {showInlineError &&
                    <FormGroup>
                        <Input hidden invalid={parseError(register?.error) ? true : false} />
                        <FormFeedback>{register?.error ? parseError(register?.error) : ''}</FormFeedback>
                    </FormGroup>
                }

                <Button color="green-light" type="submit" disabled={disabled} block className='py-2 rounded-sm mb-3' >Register</Button>
            </Form>

            <ModalUserAlreadyExists
                isOpen={showUserExistsModal}
                toggle={() => setShowUserExistsModal(false)}
            />
            <ModalInactiveUserExists
                isOpen={showInactiveUserModal}
                toggle={() => setShowInactiveUserModal(false)}
            />
        </>
    )
}
